import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import {
  hortonV3Service,
  type DreamerRequest,
  type ComposedResponse,
  type HierarchyItem,
} from '../../services/hortonV3Service'

export interface ChatMessage {
  id: string
  type: 'user' | 'horton' | 'system'
  content: string
  timestamp: Date
  session_id?: string
  v3_composed_response?: ComposedResponse
}

interface HortonChatState {
  messages: ChatMessage[]
  isLoading: boolean
  chatId: string
  sessionId: string | null
  conversationId: string | null
  turnNumber: number
  persona: string
  v3ComposedResponse: ComposedResponse | null
  hasItems: boolean
  sendMessage: (text: string) => Promise<void>
  newChat: () => void
  setPersona: (p: string) => void
}

const HortonChatContext = createContext<HortonChatState | null>(null)

export function useHortonChat(): HortonChatState {
  const ctx = useContext(HortonChatContext)
  if (!ctx) throw new Error('useHortonChat must be used inside HortonChatProvider')
  return ctx
}

function generateChatId(): string {
  return `fab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function mergeComposedResponse(
  prev: ComposedResponse | null,
  next: ComposedResponse | undefined,
): ComposedResponse | null {
  if (!next) return prev
  if (!prev) return next

  const existingItems = prev.items_summary || []
  const newItems = next.items_summary || []
  const itemsMap = new Map<string, HierarchyItem>()
  existingItems.forEach(i => itemsMap.set(`${i.type}:${i.title}`, i))
  newItems.forEach(i => itemsMap.set(`${i.type}:${i.title}`, i))

  return {
    ...next,
    items_summary: Array.from(itemsMap.values()),
    items_hierarchy: next.items_hierarchy || prev.items_hierarchy || [],
    recommendations: [...new Set([...(prev.recommendations || []), ...(next.recommendations || [])])],
    clarifying_questions: [...new Set([...(prev.clarifying_questions || []), ...(next.clarifying_questions || [])])],
    thought_process: [...(prev.thought_process || []), ...(next.thought_process || [])],
  }
}

async function persistChat(
  chatId: string,
  sessionId: string | null,
  conversationId: string | null,
  turnNumber: number,
  messages: ChatMessage[],
) {
  if (messages.length === 0) return

  const messagesToSave = messages
    .map((msg, idx) => ({
      message_index: idx,
      message_id: msg.id,
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content,
      user_message: msg.type === 'user' ? msg.content : '',
      horton_response: msg.type === 'horton' ? msg.content : '',
      v3_composed_response: msg.v3_composed_response,
      timestamp: msg.timestamp?.toISOString(),
    }))
    .filter(msg => msg.user_message || msg.horton_response)

  const sessionIdToUse = sessionId || chatId
  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  if (sessionIdToUse) headers['X-Session-ID'] = sessionIdToUse

  try {
    await fetch('/horton/chat/save', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        chat_id: chatId,
        session_id: sessionIdToUse,
        messages: messagesToSave,
        v3_conversation_id: conversationId,
        v3_turn_number: turnNumber,
      }),
    })
  } catch (err) {
    console.warn('[FloatingChat] auto-save failed', err)
  }
}

export function HortonChatProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [chatId, setChatId] = useState(generateChatId)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [turnNumber, setTurnNumber] = useState(1)
  const [persona, setPersona] = useState('supportive_best_friend')
  const [v3ComposedResponse, setV3ComposedResponse] = useState<ComposedResponse | null>(null)

  const loadingRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasItems = (v3ComposedResponse?.items_summary?.length ?? 0) > 0

  // Debounced auto-save after each message exchange completes
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      setMessages(msgs => {
        // Read latest state inside setState to avoid stale closures
        persistChat(chatId, sessionId, conversationId, turnNumber, msgs)
        return msgs // no change
      })
    }, 2000)
  }, [chatId, sessionId, conversationId, turnNumber, v3ComposedResponse])

  // Trigger save whenever messages change (and not loading)
  useEffect(() => {
    if (!isLoading && messages.length > 0) scheduleSave()
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
  }, [messages, isLoading, scheduleSave])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loadingRef.current) return
    loadingRef.current = true
    setIsLoading(true)

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: text,
      timestamp: new Date(),
    }

    const hortonId = `horton_${Date.now()}`
    const placeholder: ChatMessage = {
      id: hortonId,
      type: 'horton',
      content: '',
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMsg, placeholder])

    try {
      const history = messages.map(m => ({
        role: m.type === 'user' ? 'user' as const : 'assistant' as const,
        content: m.content,
      }))

      const request: DreamerRequest = {
        message: text,
        session_id: sessionId || undefined,
        conversation_id: conversationId || undefined,
        turn_number: turnNumber,
        persona,
        history,
      }

      const response = await hortonV3Service.runDreamer(request)

      if (response.session_id) setSessionId(response.session_id)
      if (response.conversation_id) setConversationId(response.conversation_id)
      setTurnNumber(prev => prev + 1)

      setMessages(prev =>
        prev.map(msg =>
          msg.id === hortonId
            ? {
                ...msg,
                content: response.response || 'I processed your request.',
                v3_composed_response: response.composed_response,
              }
            : msg,
        ),
      )

      if (response.composed_response) {
        setV3ComposedResponse(prev => mergeComposedResponse(prev, response.composed_response))
      }
    } catch (error: any) {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === hortonId
            ? { ...msg, content: `Something went wrong: ${error.message}` }
            : msg,
        ),
      )
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }, [messages, sessionId, conversationId, turnNumber, persona])

  const newChat = useCallback(() => {
    setMessages([])
    setChatId(generateChatId())
    setSessionId(null)
    setConversationId(null)
    setTurnNumber(1)
    setV3ComposedResponse(null)
  }, [])

  const value: HortonChatState = {
    messages,
    isLoading,
    chatId,
    sessionId,
    conversationId,
    turnNumber,
    persona,
    v3ComposedResponse,
    hasItems,
    sendMessage,
    newChat,
    setPersona,
  }

  return (
    <HortonChatContext.Provider value={value}>
      {children}
    </HortonChatContext.Provider>
  )
}
